/* NUYU virtual try-on (9 Oct 2026).
   Face tracking runs on the phone with MediaPipe Face Landmarker. No camera image leaves the device.
   Config comes from window.NUYU_TRYON, written by sections/nuyu-tryon.liquid:
   { frames: [{ code, name, img, lens, meta: { l:[x,y], r:[x,y] }, url, tints: [{ name, id, available }] }], start }
   meta.l / meta.r are the lens centres as fractions of the cutout, so the lenses can be put on the eyes. */
const MP = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14';
const MODEL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';
const TINTS = { 'Clear': null, 'Light Yellow': [250, 214, 90, 0.20], 'Yellow': [255, 196, 0, 0.36], 'Orange': [255, 112, 0, 0.44] };
const LENS_SPREAD = 1.19; // frame lens centres sit a little wider than the pupils

const cfg = window.NUYU_TRYON || { frames: [] };
const root = document.querySelector('[data-nty]');
const $ = (s) => root.querySelector(s);
const stage = $('.nty-stage'), canvas = $('.nty-canvas'), ctx = canvas.getContext('2d');
const video = $('.nty-video'), statusEl = $('.nty-status');
let landmarker = null, running = false, still = null, frame = null, tint = null, smooth = null, lastTs = -1;
const imgs = {};

function load(src) {
  if (imgs[src]) return imgs[src];
  imgs[src] = new Promise((res, rej) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => res(i); i.onerror = rej; i.src = src; });
  return imgs[src];
}
function say(t) { statusEl.textContent = t || ''; statusEl.hidden = !t; }

async function getLandmarker(mode) {
  if (!landmarker) {
    say('Loading the try-on…');
    const { FaceLandmarker, FilesetResolver } = await import(`${MP}/vision_bundle.mjs`);
    const files = await FilesetResolver.forVisionTasks(`${MP}/wasm`);
    const make = (delegate) => FaceLandmarker.createFromOptions(files, {
      baseOptions: { modelAssetPath: MODEL, delegate }, runningMode: mode, numFaces: 1,
    });
    try { landmarker = await make('GPU'); } catch (e) { console.warn('[nuyu-tryon] GPU unavailable, using CPU', e); landmarker = await make('CPU'); }
    landmarker._mode = mode;
  } else if (landmarker._mode !== mode) {
    await landmarker.setOptions({ runningMode: mode }); landmarker._mode = mode;
  }
  return landmarker;
}

/* Where the glasses go: the face points we need, in canvas pixels (z only for the true eye spacing). */
function pose(lm, W, H) {
  const P = (i) => ({ x: lm[i].x * W, y: lm[i].y * H });
  const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  let eL = mid(P(33), P(133)), eR = mid(P(362), P(263));      // centre of each eye
  let kL = P(234), kR = P(454);                                // where each ear meets the face
  if (eL.x > eR.x) { [eL, eR] = [eR, eL]; [kL, kR] = [kR, kL]; } // mirrored camera swaps sides
  const q = (i) => [lm[i].x * W, lm[i].y * H, lm[i].z * W];
  const c3 = (i, j) => q(i).map((v, n) => (v + q(j)[n]) / 2);
  const A = c3(33, 133), Bc = c3(362, 263);                    // true eye spacing, so turning the head does not shrink the frame
  return { eL, eR, nose: P(168), kL, kR, span: Math.hypot(A[0] - Bc[0], A[1] - Bc[1], A[2] - Bc[2]) };
}
/* Smoothing that only calms tiny jitters: any real movement goes straight through. */
function ease(p) {
  if (!smooth) return (smooth = JSON.parse(JSON.stringify(p)));
  const unit = Math.max(1, p.span * 0.012);
  for (const key of ['eL', 'eR', 'nose', 'kL', 'kR']) {
    const o = smooth[key], n = p[key], d = Math.hypot(n.x - o.x, n.y - o.y);
    const k = Math.min(1, 0.45 + d / unit);
    o.x += (n.x - o.x) * k; o.y += (n.y - o.y) * k;
  }
  smooth.span += (p.span - smooth.span) * Math.min(1, 0.45 + Math.abs(p.span - smooth.span) / unit);
  return smooth;
}

/* The arms: where each hinge is on the cutout, how thick, and what colour. Read once per frame image. */
const hinges = new Map();
function hingeInfo(img) {
  if (hinges.has(img)) return hinges.get(img);
  const c = document.createElement('canvas'); c.width = img.width; c.height = img.height;
  const g = c.getContext('2d', { willReadFrequently: true }); g.drawImage(img, 0, 0);
  const d = g.getImageData(0, 0, c.width, c.height).data;
  const band = Math.max(4, Math.round(img.width * 0.035));
  const side = (x0, x1) => {
    let ys = 0, n = 0, r = 0, gg = 0, b = 0, cols = 0, rows = 0;
    for (let x = x0; x < x1; x++) {
      let colRows = 0;
      for (let y = 0; y < c.height; y++) {
        const i = (y * c.width + x) * 4;
        if (d[i + 3] > 140) { ys += y; n++; r += d[i]; gg += d[i + 1]; b += d[i + 2]; colRows++; }
      }
      if (colRows) { rows += colRows; cols++; }
    }
    return n ? { x: (x0 + x1) / 2, y: ys / n, th: rows / Math.max(1, cols), rgb: [r / n, gg / n, b / n].map(Math.round) } : null;
  };
  const info = { l: side(0, band), r: side(c.width - band, c.width) };
  hinges.set(img, info); return info;
}
function drawArm(from, to, th, rgb) {
  const len = Math.hypot(to.x - from.x, to.y - from.y);
  if (len < th * 1.5) return;
  const [r, g, b] = rgb, dark = `rgb(${r * 0.6 | 0},${g * 0.6 | 0},${b * 0.6 | 0})`;
  const grad = ctx.createLinearGradient(from.x, from.y, to.x, to.y);
  grad.addColorStop(0, `rgb(${r},${g},${b})`); grad.addColorStop(1, dark);
  // tapered strip: full thickness at the hinge, thinner where it hooks over the ear
  const ux = (to.x - from.x) / len, uy = (to.y - from.y) / len, nx = -uy, ny = ux, t0 = th / 2, t1 = th * 0.34;
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,.22)'; ctx.shadowBlur = th * 0.6; ctx.shadowOffsetY = th * 0.3;
  ctx.beginPath();
  ctx.moveTo(from.x + nx * t0, from.y + ny * t0);
  ctx.lineTo(to.x + nx * t1, to.y + ny * t1);
  ctx.arc(to.x, to.y, t1, Math.atan2(ny, nx), Math.atan2(-ny, -nx));
  ctx.lineTo(from.x - nx * t0, from.y - ny * t0);
  ctx.closePath(); ctx.fillStyle = grad; ctx.fill();
  ctx.restore();
}

/* The frame with its tinted lenses, built once per frame + lens choice. */
const comps = new Map();
function composite(img, lens) {
  const key = img.src + '|' + (tint && tint.name);
  if (comps.has(key)) return comps.get(key);
  const c = document.createElement('canvas'); c.width = img.width; c.height = img.height;
  const o = c.getContext('2d'), t = TINTS[tint && tint.name];
  if (t) {
    o.fillStyle = `rgba(${t[0]},${t[1]},${t[2]},${t[3]})`; o.fillRect(0, 0, c.width, c.height);
    o.globalCompositeOperation = 'destination-in'; o.drawImage(lens, 0, 0);
    o.globalCompositeOperation = 'source-over';
  }
  o.drawImage(img, 0, 0);
  comps.set(key, c); return c;
}

async function drawGlasses(p) {
  if (!frame) return;
  const [img, lens] = await Promise.all([load(frame.img), load(frame.lens)]);
  const m = frame.meta, W = img.width, H = img.height;
  const lx = m.l[0] * W, rx = m.r[0] * W, cx = (lx + rx) / 2, cy = ((m.l[1] + m.r[1]) / 2) * H;
  const s = (p.span * LENS_SPREAD) / (rx - lx);                 // height of the frame follows the true eye spacing
  // face axes: u along the eyes, v down the face, origin between the eyes
  const o = { x: (p.eL.x + p.eR.x) / 2, y: (p.eL.y + p.eR.y) / 2 };
  const ang = Math.atan2(p.eR.y - p.eL.y, p.eR.x - p.eL.x), ax = Math.cos(ang), ay = Math.sin(ang);
  const U = (pt) => (pt.x - o.x) * ax + (pt.y - o.y) * ay;
  const uL = U(p.eL), uR = U(p.eR), bu = U(p.nose);              // bridge sits on the nose
  // a turned head narrows the whole frame evenly (never wider than the real frame); the bridge stays on the nose
  const turn = Math.max(0.45, Math.min(1, (uR - uL) / p.span));
  const sxL = s * turn, sxR = s * turn;
  const B = new DOMMatrix().translate(o.x, o.y).rotate(ang * 180 / Math.PI);

  // arms first, so the front of the frame sits over them; they run back almost level to the top of the ear
  const hi = hingeInfo(img);
  ctx.save(); ctx.setTransform(B);
  for (const [h, ear, sx, dir] of [[hi.l, p.kL, sxL, -1], [hi.r, p.kR, sxR, 1]]) {
    if (!h) continue;
    const from = { x: bu + (h.x - cx) * sx, y: (h.y - cy) * s };
    const to = { x: U(ear), y: from.y + p.span * 0.04 };
    if ((to.x - from.x) * dir > p.span * 0.08) drawArm(from, to, Math.max(3, h.th * s * 0.8), h.rgb); // the far arm hides behind the head
  }
  ctx.restore();

  const comp = composite(img, lens);
  for (const [sx, x0, x1] of [[sxL, 0, cx], [sxR, cx, W]]) {
    ctx.save();
    ctx.setTransform(B.translate(bu, 0).scale(sx, s).translate(-cx, -cy));
    ctx.beginPath(); ctx.rect(x0 - (x0 ? 0.5 : 0), 0, x1 - x0 + 0.5, H); ctx.clip();
    ctx.shadowColor = 'rgba(0,0,0,.25)'; ctx.shadowBlur = 6; ctx.shadowOffsetY = 3;
    ctx.drawImage(comp, 0, 0);
    ctx.restore();
  }
}

function fit(w, h) { canvas.width = w; canvas.height = h; }

async function loop() {
  if (!running) return;
  if (video.readyState >= 2 && video.currentTime !== lastTs) {
    lastTs = video.currentTime;
    const W = video.videoWidth, H = video.videoHeight;
    if (canvas.width !== W || canvas.height !== H) fit(W, H);
    ctx.save(); ctx.translate(W, 0); ctx.scale(-1, 1); ctx.drawImage(video, 0, 0, W, H); ctx.restore(); // mirror, like a mirror
    const r = landmarker.detectForVideo(video, performance.now());
    if (r.faceLandmarks && r.faceLandmarks[0]) {
      const lm = r.faceLandmarks[0].map((q) => ({ x: 1 - q.x, y: q.y, z: -q.z }));
      await drawGlasses(ease(pose(lm, W, H))); say('');
    } else { smooth = null; say('Look at the camera'); }
  }
  requestAnimationFrame(loop);
}

async function startCamera() {
  still = null;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 960 }, height: { ideal: 720 } }, audio: false });
    video.srcObject = stream; await video.play();
    await getLandmarker('VIDEO');
    root.classList.add('is-live'); running = true; say(''); loop();
  } catch (e) {
    console.warn('[nuyu-tryon] camera', e);
    say('No camera? Upload a selfie instead.'); root.classList.add('is-nocam');
  }
}
function stopCamera() {
  running = false;
  const s = video.srcObject; if (s) s.getTracks().forEach((t) => t.stop());
  video.srcObject = null; root.classList.remove('is-live');
}
/* Close: camera off, uploaded selfie cleared, screen back to its start (the demo story plays again). */
function closeTryOn() {
  stopCamera();
  still = null; smooth = null;
  root.classList.remove('is-still', 'is-nocam');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const f = $('.nty-file'); if (f) f.value = '';
  say('');
}

async function renderStill() {
  if (!still) return;
  const W = still.naturalWidth, H = still.naturalHeight;
  fit(W, H); ctx.drawImage(still, 0, 0);
  const lmk = await getLandmarker('IMAGE');
  const r = lmk.detect(still);
  if (r.faceLandmarks && r.faceLandmarks[0]) { smooth = null; await drawGlasses(ease(pose(r.faceLandmarks[0], W, H))); say(''); }
  else say('We could not find a face. Try a photo looking straight at the camera.');
}
async function useFile(file) {
  stopCamera();
  const url = URL.createObjectURL(file);
  still = await load(url); root.classList.add('is-still'); await renderStill();
}

/* Frames and tints */
function pickFrame(code) {
  frame = cfg.frames.find((f) => f.code === code) || cfg.frames[0];
  root.querySelectorAll('.nty-frame').forEach((b) => b.setAttribute('aria-pressed', b.dataset.code === frame.code));
  $('.nty-name').textContent = frame.name;
  const box = $('.nty-tints'); box.innerHTML = '';
  frame.tints.forEach((t, i) => {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'nty-tint'; b.dataset.i = i;
    b.innerHTML = `<span class="nty-dot" style="--c:${TINTS[t.name] ? `rgb(${TINTS[t.name].slice(0, 3)})` : '#fff'}"></span>${t.name}`;
    if (!t.available) { b.disabled = true; b.title = 'Sold out'; }
    b.addEventListener('click', () => pickTint(i));
    box.appendChild(b);
  });
  const first = frame.tints.findIndex((t) => t.available);
  pickTint(first < 0 ? 0 : first);
  if (running || still) { load(frame.img); load(frame.lens); } // nothing heavy loads before the visitor taps
}
function pickTint(i) {
  tint = frame.tints[i];
  root.querySelectorAll('.nty-tint').forEach((b) => b.setAttribute('aria-pressed', +b.dataset.i === i));
  const add = $('.nty-add');
  add.disabled = !tint || !tint.available;
  add.textContent = tint && tint.available ? `Add to basket · ${tint.price}` : 'Sold out';
  if (still) renderStill();
}

/* Add to basket the way the rest of the site does: ask Shopify for the redrawn cart drawer in the same call,
   then hand it to the theme's <cart-drawer>, which redraws and opens itself. /cart is not used on this site. */
async function addToBasket() {
  if (!tint || !tint.available) return;
  const add = $('.nty-add'); add.disabled = true; add.textContent = 'Adding…';
  const drawer = document.querySelector('cart-drawer');
  const body = { items: [{ id: tint.id, quantity: 1 }] };
  if (drawer && drawer.getSectionsToRender) {
    body.sections = drawer.getSectionsToRender().map((s) => s.id);
    body.sections_url = location.pathname;
    if (drawer.setActiveElement) drawer.setActiveElement(add);
  }
  try {
    const r = await fetch(((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
    });
    const j = await r.json();
    if (!r.ok || j.status) throw new Error(j.description || j.message || r.status);
    if (drawer && drawer.renderContents) {
      drawer.classList.remove('is-empty');
      const item = j.items && j.items[0] ? j.items[0] : j;
      item.sections = j.sections;
      drawer.renderContents(item);
      add.textContent = 'Added ✓';
    } else {
      add.textContent = 'Added ✓';
      $('.nty-basket').hidden = false;
    }
  } catch (e) { console.warn('[nuyu-tryon] add', e); add.textContent = 'Try again'; }
  setTimeout(() => { add.disabled = false; add.textContent = `Add to basket · ${tint.price}`; }, 1600);
}

/* "View basket" opens the drawer too; it only falls back to the cart page if the theme has no drawer. */
function openBasket(e) {
  const drawer = document.querySelector('cart-drawer');
  if (drawer && drawer.open) { e.preventDefault(); drawer.open(e.currentTarget); }
}

async function snap() {
  canvas.toBlob(async (blob) => {
    if (!blob) return;
    const file = new File([blob], `nuyu-${frame.code}.jpg`, { type: 'image/jpeg' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], title: 'Which NUYU frames?', text: `Trying on the NUYU Afterlight ${frame.name}` }); return; } catch (e) { if (e.name === 'AbortError') return; }
    }
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = file.name; a.click();
  }, 'image/jpeg', 0.9);
}

root.querySelectorAll('.nty-frame').forEach((b) => b.addEventListener('click', () => pickFrame(b.dataset.code)));
$('.nty-start').addEventListener('click', startCamera);
$('.nty-file').addEventListener('change', (e) => e.target.files[0] && useFile(e.target.files[0]));
$('.nty-add').addEventListener('click', addToBasket);
$('.nty-basket').addEventListener('click', openBasket);
$('.nty-snap').addEventListener('click', snap);
$('.nty-close').addEventListener('click', closeTryOn);
document.addEventListener('visibilitychange', () => { if (document.hidden && running) closeTryOn(); }); // left the tab or locked the phone
// start on the frame a product page sent us to (?p=<product handle>) or ?frame=<code>
const qs = new URLSearchParams(location.search);
const sent = cfg.frames.find((f) => (qs.get('p') && (f.handles || []).includes(qs.get('p'))) || f.code === qs.get('frame'));
pickFrame((sent && sent.code) || cfg.start || (cfg.frames[0] && cfg.frames[0].code));
const lensWanted = qs.get('lens');
// kit page: start on the kit's selected lens too
if (!lensWanted && cfg.startLens && frame) { const i = frame.tints.findIndex((t) => t.name === cfg.startLens && t.available); if (i >= 0) pickTint(i); }
if (lensWanted && frame) { const i = frame.tints.findIndex((t) => t.name.toLowerCase() === lensWanted.toLowerCase() && t.available); if (i >= 0) pickTint(i); }
if (sent) { const el = root.querySelector(`.nty-frame[data-code="${sent.code}"]`), strip = $('.nty-frames'); if (el) strip.scrollLeft = el.offsetLeft - strip.offsetLeft - 12; } // scroll the strip only, not the page
// Hide the floating Hub button while the try-on is on screen (it sits over the screen's corner and the frame strip).
if ('IntersectionObserver' in window) {
  new IntersectionObserver((es) => es.forEach((e) => {
    document.body.classList.toggle('nty-in-view', e.isIntersecting);
    if (!e.isIntersecting && running) closeTryOn(); // scrolled away: the camera goes off
  }), { threshold: 0.15 }).observe(root);
}
window.__nty = { pickFrame, pickTint, useFile }; // for testing
